import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import BetterBarcodeScanner from '../components/BetterBarcodeScanner';
import { MobileContainer, Card, Button, Input, Label, FormGroup, Logo } from '../theme/GlobalStyles';
import { FaQrcode, FaKeyboard, FaUserPlus } from 'react-icons/fa';
import cdePlusLogo from '../assets/images/cde_plus_logo.png';
import cocaColaLogo from '../assets/images/logoCocacola.png';
import barcodeIcon from '../assets/images/barcodeicon.jpg';
import CustomAlert from '../components/CustomAlert';
import LoadingSpinner from '../components/LoadingSpinner';
import authService from '../services/authService';
import { FORM_NAMES } from '../services/webApiFormService';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 1.5rem 0.75rem 1rem;
  background: ${props => props.theme.background};
  width: 100%;

  @media (max-width: 768px) {
    padding: 0.5rem 0.5rem 0.5rem;
  }
`;

const ContentCard = styled(Card)`
  width: 100%;
  max-width: 400px;
  padding: 1.5rem;
  text-align: center;

  @media (max-width: 768px) {
    max-width: 100%;
    border-radius: 0;
    padding: 0.75rem 1rem 1rem;
    background: transparent;
    box-shadow: none;
    border: none;
    min-height: auto;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 0.6rem;
  }
`;

const LogoContainer = styled.div`
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 768px) {
    margin-bottom: 0.5rem;
  }
`;

const CocaColaBrandLogo = styled.img`
  height: 94px;
  width: auto;
  object-fit: contain;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    height: 80px;
    margin-bottom: 1rem;
  }
`;

const CdePlusBrandLogo = styled(Logo)`
  height: 64px;
  padding: 9px 16px;

  @media (max-width: 768px) {
    height: 60px;
    padding: 8px 14px;
  }
`;

const ScanIcon = styled.div`
  margin: 0.5rem 0 0.75rem;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  @media (max-width: 768px) {
    margin: 0.3rem 0 0.5rem;
  }

  img {
    width: 96px;
    height: 96px;
    object-fit: contain;

    @media (max-width: 768px) {
      width: 160px;
      height: 110px;
    }
  }
`;

const InstructionText = styled.p`
  color: ${props => props.theme.textSecondary};
  font-size: 0.85rem;
  margin-bottom: 0.75rem;
  line-height: 1.5;

  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin-bottom: 0.5rem;
    line-height: 1.4;
  }
`;

const PrimaryButton = styled(Button)`
  background: ${props => props.theme.primary};
  color: white;
  padding: 0.6rem 1.65rem;
  border-radius: 25px;
  font-size: 1.6rem;
  font-weight: 600;
  min-width: 120px;
  margin: 0.45rem auto;
  display: block;
  border: none;

  @media (max-width: 768px) {
    padding: 0.5rem 1.2rem;
    font-size: 0.88rem;
    margin: 0.35rem auto;
  }

  &:hover:not(:disabled) {
    background: ${props => props.theme.primaryDark};
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(PrimaryButton)`
  background: transparent;
  color: ${props => props.theme.primary};
  border: 2px solid ${props => props.theme.primary};
  
  &:hover:not(:disabled) {
    background: ${props => props.theme.primary};
    color: white;
  }
`;

const OrDivider = styled.div`
  text-align: center;
  margin: 0.5rem 0;
  position: relative;

  @media (max-width: 768px) {
    margin: 0.4rem 0;
  }

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: ${props => props.theme.border};
  }

  span {
    background: white;
    padding: 0 1rem;
    position: relative;
    color: ${props => props.theme.textSecondary};
    font-size: 0.8rem;
  }
`;

const ManualEntryInput = styled(Input)`
  text-align: center;
  margin-bottom: 0.5rem;
  padding: 0.6rem;
  font-size: 1.3rem;
`;

const RegistrationSection = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid ${props => props.theme.border};

  @media (max-width: 768px) {
    margin-top: 0.75rem;
    padding-top: 0.75rem;
  }
`;

const RegistrationLink = styled.a`
  color: ${props => props.theme.primary};
  text-decoration: none;
  font-weight: 600;
  font-size: 0.85rem;
  display: inline-block;
  margin-bottom: 0.75rem;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ScannerOverlay = styled.div<{ isVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: ${props => props.isVisible ? 'flex' : 'none'};
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 2rem;
`;

const ScannerBox = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  width: 100%;
  max-width: 400px;
`;

const ScannerTitle = styled.h3`
  color: ${props => props.theme.primary};
  margin-bottom: 1rem;
  text-align: center;
`;

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScanning, setIsScanning] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [scannedValue, setScannedValue] = useState('');

  const [alert, setAlert] = useState<{
    isVisible: boolean;
    type?: 'success' | 'error' | 'warning' | 'info' | 'confirm';
    title?: string;
    message: string;
    onConfirm?: () => void;
    onClose?: () => void;
    confirmText?: string;
    cancelText?: string;
  }>({ isVisible: false, message: '' });

  // Show session expired popup if redirected due to 401
  useEffect(() => {
    const sessionExpired = sessionStorage.getItem('sessionExpired');
    if (sessionExpired === 'true') {
      sessionStorage.removeItem('sessionExpired');
      setAlert({
        isVisible: true,
        type: 'warning',
        title: 'Session Expired',
        message: 'Your session has expired. Please scan the equipment again to login.'
      });
    }
  }, []);

  // Auto-validate after registration redirect (skip re-scan)
  useEffect(() => {
    const state = location.state as { autoValidateSerial?: string } | null;
    if (state?.autoValidateSerial) {
      // Clear the state so it doesn't re-trigger on refresh
      window.history.replaceState({}, document.title);
      validateCustomer(state.autoValidateSerial);
    }
  }, []);

  const startScanning = () => {
    setIsScanning(true);
  };

  const stopScanning = () => {
    setIsScanning(false);
  };

  const openRegistrationForm = (serialNumber?: string) => {
    navigate(`/dynamic-form/${encodeURIComponent(FORM_NAMES.CUSTOMER_UNIT_REGISTRATION)}`, { 
      state: { prefillSerialNumber: serialNumber } 
    });
  };

  const validateCustomer = async (rawInput: string) => {
    // Strip cdesticker URL prefix if present
    let greenTagNumber = rawInput;
    if (rawInput.includes('cdesticker.ccbp.com.ph')) {
      // Extract the value after the URL (e.g. https://cdesticker.ccbp.com.ph/ABC123 -> ABC123)
      const parts = rawInput.split('cdesticker.ccbp.com.ph');
      const extracted = parts[parts.length - 1].replace(/^\/+/, '').trim();
      if (!extracted) {
        // URL only, no serial number attached
        setAlert({
          isVisible: true,
          type: 'error',
          title: 'Invalid QR Code',
          message: 'This QR code is not valid. Please scan the correct Green Tag / Serial Number.'
        });
        return;
      }
      greenTagNumber = extracted;
    }

    setIsLoading(true);
    try {
      const response = await authService.validateGreenTag(greenTagNumber);

      if (response.success) {
        if (response.sales) {
          // Sales user login - store sales data
          navigate('/dashboard');
        }
        // NEW: Handle underRegistration case
        else if (response.underRegistration && response.registration) {
          // Registration exists but not closed - just show alert, don't navigate
          const registration = response.registration as any;

          setAlert({
            isVisible: true,
            type: 'warning',
            title: 'Registration In Progress',
            message: `This Green Tag is currently under registration process.\nStatus: ${registration.status || 'Pending'}\n\nPlease wait for the registration to be completed.`
          });
        }
        else if (response.exists && response.customer) {
          // Customer exists - store customer data
          const customer = response.customer as any; // Use any to handle dynamic properties

          // Store customer code, name, role, and org data
          localStorage.setItem('customerCode', customer.code || customer.Code || '');
          localStorage.setItem('customerName', customer.name || customer.Name || '');
          localStorage.setItem('serialNumber', customer.serialno || customer.SerialNo || customer.serialNo || greenTagNumber);
          localStorage.setItem('greenTagNumber', customer.greentag || customer.Greentag || customer.greenTag || greenTagNumber);

          // Check if customer has completed registration
          const registrationStatus = customer.registrationStatus || customer.RegistrationStatus;
          const serialNumber = customer.serialno || customer.SerialNo || customer.serialNo;
          const cdeVerificationUID = customer.cdeVerificationUID || customer.CDEVerificationUID;

          // Always navigate to dashboard
          if (serialNumber) {
            localStorage.setItem('serialNumber', serialNumber);
          }

          // Show toast if verification is incomplete
          if (!cdeVerificationUID || cdeVerificationUID === '0' || cdeVerificationUID === 'null') {
            sessionStorage.setItem('verificationIncomplete', 'true');
          }

          navigate('/dashboard');
        } else {
          // Neither sales nor valid customer
          setAlert({
            isVisible: true,
            type: 'confirm',
            title: 'Alert',
            message: 'Invalid serial number\nplease register',
            onConfirm: () => {
              openRegistrationForm(greenTagNumber);
            },
            onClose: () => {
              setAlert({ isVisible: false, message: '' });
            },
            confirmText: 'Register',
            cancelText: 'Cancel'
          });
        }
      } else {
        // Show registration prompt for invalid green tag
        setAlert({
          isVisible: true,
          type: 'confirm',
          title: 'Alert',
          message: 'Invalid serial number\nplease register',
          onConfirm: () => {
            openRegistrationForm(greenTagNumber);
          },
          onClose: () => {
            setAlert({ isVisible: false, message: '' });
          },
          confirmText: 'Register',
          cancelText: 'Cancel'
        });
      }
    } catch (error: any) {
      console.error('Validation error:', error);

      // Check if it's an authentication error
      if (error.message && (error.message.includes('Authentication') || error.message.includes('Access denied'))) {
        // Show authentication error message from the API
        setAlert({
          isVisible: true,
          type: 'error',
          title: 'Authentication Failed',
          message: error.message
        });
      } else if (error.response?.status === 404 || error.response?.status === 400) {
        // Show clean error message for invalid serial number
        setAlert({
          isVisible: true,
          type: 'confirm',
          title: 'Alert',
          message: 'Invalid serial number\nplease register',
          onConfirm: () => {
            openRegistrationForm(greenTagNumber);
          },
          onClose: () => {
            setAlert({ isVisible: false, message: '' });
          },
          confirmText: 'Register',
          cancelText: 'Cancel'
        });
      } else if (error.response?.status === 500) {
        // Server error
        setAlert({
          isVisible: true,
          type: 'error',
          title: 'Service Error',
          message: 'Service temporarily unavailable. Please try again later.'
        });
      } else if (error.code === 'ERR_NETWORK') {
        // Network error
        setAlert({
          isVisible: true,
          type: 'error',
          title: 'Connection Error',
          message: 'Unable to connect to server. Please check your internet connection.'
        });
      } else {
        // Generic error fallback
        setAlert({
          isVisible: true,
          type: 'confirm',
          title: 'Alert',
          message: 'Invalid serial number\nplease register',
          onConfirm: () => {
            openRegistrationForm(greenTagNumber);
          },
          onClose: () => {
            setAlert({ isVisible: false, message: '' });
          },
          confirmText: 'Register',
          cancelText: 'Cancel'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualSubmit = () => {
    const trimmedInput = manualInput.trim();
    if (!trimmedInput) {
      setAlert({
        isVisible: true,
        type: 'warning',
        title: 'Input Required',
        message: 'Please enter a Green Tag or Serial Number'
      });
      return;
    }
    // Same logic as scanning - validate and route based on response
    validateCustomer(trimmedInput);
  };

  const handleRegister = () => {
    openRegistrationForm();
  };

  return (
    <PageContainer>
      {isLoading && <LoadingSpinner isVisible={isLoading} />}
      
      <ContentCard>
        <LogoContainer>
          <CocaColaBrandLogo src={cocaColaLogo} alt="Coca-Cola" />
          <CdePlusBrandLogo src={cdePlusLogo} alt="Coca-Cola Philippines" />
        </LogoContainer>

        <ScanIcon onClick={startScanning}>
          <img src={barcodeIcon} alt="Barcode Scanner" />
        </ScanIcon>

        <InstructionText>
          Click the scanner image or scan button to scan the Green Tag / Enter Serial Number to Login
        </InstructionText>

        <PrimaryButton type="button" onClick={startScanning}>
          Scan Barcode
        </PrimaryButton>

        <OrDivider>
          <span>OR</span>
        </OrDivider>

        <form onSubmit={(e) => {
          e.preventDefault();
          handleManualSubmit();
        }}>
          <ManualEntryInput
            type="text"
            placeholder="Enter Inventory Number"
            value={manualInput}
            onChange={(e) => setManualInput(e.target.value)}
          />

          <PrimaryButton 
            type="submit"
            disabled={!manualInput.trim() || isLoading}
          >
            Submit
          </PrimaryButton>
        </form>

        {/* <RegistrationSection>
          <div style={{ marginBottom: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
            Customer Unit Registration →
          </div>
          
          <PrimaryButton
            type="button"
            onClick={openRegistrationForm}
            style={{ background: 'transparent', color: '#F40000', border: '2px solid #F40000' }}
          >
            Register Now
          </PrimaryButton>
        </RegistrationSection> */}
      </ContentCard>

      <ScannerOverlay isVisible={isScanning}>
        <ScannerBox>
          <ScannerTitle>Scanning QR Code...</ScannerTitle>
          <BetterBarcodeScanner
            isActive={isScanning}
            onScan={(result) => {
              if (result && result.trim()) {
                setScannedValue(result);
                setIsScanning(false);
                validateCustomer(result);
              }
            }}
            onError={(error) => {
              console.error('Scanner error:', error);
              setIsScanning(false);
              
              if (error.name === 'NotAllowedError' || error.message?.includes('Permission')) {
                setAlert({
                  isVisible: true,
                  type: 'warning',
                  title: 'Camera Permission Required',
                  message: 'Please allow camera access to scan QR codes.'
                });
              } else {
                setAlert({
                  isVisible: true,
                  type: 'error',
                  title: 'Scanner Error',
                  message: 'Failed to start camera. Please try manual entry.'
                });
              }
            }}
            onCancel={stopScanning}
          />
        </ScannerBox>
      </ScannerOverlay>

      <CustomAlert
        isVisible={alert.isVisible}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={alert.onClose || (() => setAlert({ isVisible: false, message: '' }))}
        onConfirm={alert.onConfirm}
        confirmText={alert.confirmText}
        cancelText={alert.cancelText}
      />
    </PageContainer>
  );
};

export default HomePage;